/**
 * @file: 字码商品dom节点构造
 * @author: xiaolong.huang@dmall.com
 */

import * as Toolkit from '../../../toolkit/index';
import * as Utils from '../../../utils/util';
import * as UIToolkit from '../../../UIToolkit/index';
import Config from '../../../config';
import { SubGoodsRenderData } from './renderData';
import CreateGoodsImgUI from './createGoodsImgUI';

interface Color {
    background: string,
    font: string
}

/**
 * 多个字码商品dom节点构造，每个sub-good-item代表一个字码商品
 *
 * 返回结构示例:
 * <div class="sub-good-item">
 *     <div class="sub_tags sub_*"></div>
 *     <ul>
 *         <li class="goods-table-td" style="...">
 *             <div class="goods-item">
 *                 <img alt="图片加载中..." src="..."  data-good="1">
 *             </div>
 *         </li>
 *         <li class="goods-table-td" style="...">
 *         ...循环
 *     </ul>
 *     <ul>
 *     ...循环
 * </div>
 * <div class="sub-good-item">
 * ...循环
 */
function createSubGoodsDom(renderData: SubGoodsRenderData): JQuery<HTMLElement> {
    let $result: JQuery<HTMLElement> = $(document.createElement('div'));

    for (let i: number = renderData.subSkuList.length - 1; i > 0; i--) {
        let sku: SkuData = renderData.subSkuList[i];
        let color: Color = getGoodsColor(sku);

        /**
         * $singleGoods: 单个字码商品陈列位
         *
         * 结构:
         * <li class="goods-table-td" style="...">
         *    <div class="goods-item">
         *         <img alt="图片加载中..." src="..."  data-good="1">
         *    </div>
         * </li>
         */
        let $singleGoods: JQuery<HTMLElement> = createSingleGoods({
            sku,
            direction: renderData.direction,
            rotate: renderData.rotate
        });

        /**
         * $singleGoodsGroup: 组合多个$singleGoods，构成一个具有面宽、面高的字码商品完整陈列位
         * ul数量: 面高数
         * $singleGoods数量: 面宽数
         *
         * 返回结构:
         * <div class="sub-good-item">
         *     <div class="sub_tags sub_*"></div>
         *     <ul>
         *         ...$singleGoods循环
         *     </ul>
         *     ...ul循环
         * </div>
         */
        let $singleGoodsGroup: JQuery<HTMLElement> = createSingleSubGoodsGroup({
            mainGoodsSize: renderData.mainGoodsSize,
            $singleGoods,
            sku,
            posWidth: renderData.posWidth,
            index: i,
            lightColor: color
        });

        $result.append($singleGoodsGroup);
    }

    return $result.children();
}

/**
 * 创建单个字码商品陈列位
 *
 * 返回dom结构:
 *
 * <li class="goods-table-td" style="...">
 *    <div class="goods-item">
 *         <img alt="图片加载中..." src="..."  data-good="1">
 *    </div>
 * </li>
 */
interface CreateSingleGoodsParams {
    // 商品信息
    sku: SkuData,

    // 商品方位
    direction: number,

    // 商品旋转角度
    rotate: number
}
function createSingleGoods(params: CreateSingleGoodsParams): JQuery<HTMLElement> {
    /**
     * $singleGoodsBox结构:
     *
     * <li class="goods-table-td" style="..."></li>
     */
    let $singleGoodsBox: JQuery<HTMLElement> = createSingleGoodsBox({
        sku: params.sku
    });

    /**
     * $goodsItem结构:
     *
     * <div class="goods-item">
     *     <img alt="图片加载中..." src="..."  data-good="1">
     * </div>
     */
    let $goodsItem: JQuery<HTMLElement> = createGoodsItem({
        sku: params.sku,
        direction: params.direction,
        rotate: params.rotate
    });

    $singleGoodsBox.append($goodsItem);

    return $singleGoodsBox;
}

/**
 * 创建单个字码商品陈列位容器
 *
 * 返回结构: <li class="goods-table-td" style="..."></li>
 */
interface CreateSingleGoodsBoxParams {
    sku: SkuData
}
function createSingleGoodsBox(params: CreateSingleGoodsBoxParams): JQuery<HTMLElement> {
    let $result: JQuery<HTMLElement> = $(document.createElement('li')).addClass('goods-table-td');
    let style: FreeObjMap = {
        width: Toolkit.cmToPx((params.sku.width || 10)),
        height: Toolkit.cmToPx((params.sku.height || 20)),
        background: params.sku.backColor
    };

    if (UIToolkit.isPicModel()) {
        style.background = 'transparent';
    }
    else {
        style.border = '';
    }

    $result.css(style);

    return $result;
}

/**
 * 创建单个字码商品陈列位
 *
 * 返回结构:
 * <div class="goods-item">
 *     <img alt="图片加载中..." src="..."  data-good="1">
 * </div>
 */
function createGoodsItem(params): JQuery<HTMLElement> {
    let $goodsItem: JQuery<HTMLElement> = $(document.createElement('div')).addClass('goods-item');

    $goodsItem.css({
        width: '100%',
        height: '100%'
    });

    let $goodsImgUI: JQuery<HTMLElement> = CreateGoodsImgUI({
        width: params.sku.width,
        height: params.sku.height,
        direction: params.direction,
        rotate: params.rotate,
        imgMap: {
            [params.direction]: params.sku.imgUri
        }
    });

    $goodsItem.append($goodsImgUI);

    return $goodsItem;
}

/**
 * 组合多个$singleGoods，构成一个具有面宽、面高的字码商品完整陈列位
 *
 * 返回结构:
 * <div class="sub-good-item">
 *     <div class="sub_tags sub_*"></div>
 *     <ul>
 *         <li class="goods-table-td" style="...">
 *             <div class="goods-item">
 *                 <img alt="图片加载中..." src="..."  data-good="1">
 *             </div>
 *         </li>
 *         ...li循环
 *     </ul>
 *     ...ul循环
 * </div>
 */
interface CreateSingleSubGoodsGroupParams {
    mainGoodsSize: PhysicsSize,
    $singleGoods: JQuery<HTMLElement>,
    sku: SkuData,
    posWidth: number,
    index: number,
    lightColor?: Color
}
function createSingleSubGoodsGroup(params: CreateSingleSubGoodsGroupParams): JQuery<HTMLElement> {
    let $subSkuFloor: JQuery<HTMLElement> = $(document.createElement('div')).addClass('sub-good-item').attr('data-subindex', params.index - 1);
    let $tag: JQuery<HTMLElement> = $(document.createElement('div')).addClass(`sub_tags hide sub_${params.index}`);

    if (UIToolkit.isTxTModel() || UIToolkit.isColorModel()) {
        // 字码商品文字显示
        $tag.removeClass('hide').addClass('show');
    }
    else {
        // 字码商品文字隐藏
        $tag.removeClass('show').addClass('hide');
    }

    $tag.css({
        background: params.lightColor.background,
        color: params.lightColor.font
    });
    $subSkuFloor.append($tag);
    $subSkuFloor.css({
        position: 'relative'
    });

    // 构建字码商品1个面高dom节点，其中包含多个面宽
    createRowDom({
        mainGoodsSize: params.mainGoodsSize,
        sku: params.sku,
        $singleGoods: params.$singleGoods,
        posWidth: params.posWidth,
        returnRow($row) {
            $subSkuFloor.append($row);
        }
    });

    return $subSkuFloor;
}

/**
 * 构建字码商品1个面高dom节点，其中包含多个面宽
 *
 * 返回结构:
 * <ul>
 *     <li class="goods-table-td" style="...">
 *         <div class="goods-item">
 *             <img alt="图片加载中..." src="..."  data-good="1">
 *         </div>
 *     </li>
 *     ...li循环
 * </ul>
 */
function createRowDom(params: FreeObjMap): void {
    let width: PhysicsMeasurement = Utils.toFixedNumber(params.sku.width, 2);
    let height: PhysicsMeasurement = Utils.toFixedNumber(params.sku.height, 2);
    let row: number = Utils.toNumberForce(params.sku.surfaceHeight) || 1;
    let col: number = Utils.toNumberForce(params.sku.surfaceWidth) || 1;
    let offset: PhysicsMeasurement = 0;

    if (params.posWidth) {
        offset = params.posWidth;
        offset = Utils.toFixedNumber(offset - (width - params.mainGoodsSize.width), 2);
    }

    for (let r: number = 0; r < row; r++) {
        let $row: JQuery = $(document.createElement('ul')).addClass('sub-goods-row');

        for (let f: number = 0; f < col; f++) {
            let $singleGoodsClone: JQuery = params.$singleGoods.clone(true);

            if (params.posWidth && f < col - 1) {
                $singleGoodsClone.css({
                    marginRight: `${Toolkit.cmToPx(offset)}px`
                });
            }

            $row.append($singleGoodsClone);
        }

        params.returnRow && params.returnRow($row);
    }
}

// 色值计算
function getGoodsColor(sku: SkuData): Color {
    // 对应渲染的品牌数据库
    let brandData: FreeObjMap|null = Config.getGlobalConfig('BRAND_DATA') || null;

    // 对应渲染的品牌数据库
    let benderBrandAndEnum: FreeObjMap|null = Config.getGlobalConfig('RENDER_BRAND_ENUM') || null;
    let result: Color = {
        background: '#fff000',
        font: '#000'
    };

    if (brandData[sku.skuId] && benderBrandAndEnum[brandData[sku.skuId]]) {
        result.background = benderBrandAndEnum[brandData[sku.skuId]].color;
        result.font = benderBrandAndEnum[brandData[sku.skuId]].fontColor;
    }

    return result;
}

export default createSubGoodsDom;
